import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Minus, BarChart3Icon, CalendarIcon } from 'lucide-react';
type Timeframe = '7d' | '30d' | '90d' | 'ytd';
interface MetricData {
  label: string;
  values: Record<Timeframe, number>;
  trends: Record<Timeframe, number>;
  previousValues: Record<Timeframe, number>;
  averages: Record<Timeframe, number>;
}
const metricsData: MetricData[] = [{
  label: 'Applications Taken',
  values: {
    '7d': 12,
    '30d': 48,
    '90d': 132,
    ytd: 410
  },
  trends: {
    '7d': 15,
    '30d': -5,
    '90d': 8,
    ytd: 12
  },
  previousValues: {
    '7d': 10,
    '30d': 51,
    '90d': 122,
    ytd: 366
  },
  averages: {
    '7d': 10.4,
    '30d': 50.5,
    '90d': 128,
    ytd: 390
  }
}, {
  label: 'Pre-Approvals Issued',
  values: {
    '7d': 8,
    '30d': 35,
    '90d': 98,
    ytd: 305
  },
  trends: {
    '7d': 10,
    '30d': 8,
    '90d': 5,
    ytd: 14
  },
  previousValues: {
    '7d': 7,
    '30d': 32,
    '90d': 93,
    ytd: 267
  },
  averages: {
    '7d': 7.3,
    '30d': 32.4,
    '90d': 95,
    ytd: 280
  }
}, {
  label: 'Closings',
  values: {
    '7d': 5,
    '30d': 22,
    '90d': 61,
    ytd: 198
  },
  trends: {
    '7d': -8,
    '30d': 12,
    '90d': 3,
    ytd: 9
  },
  previousValues: {
    '7d': 5,
    '30d': 20,
    '90d': 59,
    ytd: 182
  },
  averages: {
    '7d': 5.4,
    '30d': 19.6,
    '90d': 58,
    ytd: 185
  }
}, {
  label: 'Disclosures Sent',
  values: {
    '7d': 8,
    '30d': 31,
    '90d': 85,
    ytd: 270
  },
  trends: {
    '7d': 2,
    '30d': -3,
    '90d': 6,
    ytd: 11
  },
  previousValues: {
    '7d': 8,
    '30d': 32,
    '90d': 80,
    ytd: 243
  },
  averages: {
    '7d': 7.8,
    '30d': 32,
    '90d': 82,
    ytd: 255
  }
}, {
  label: 'Conversion Rate',
  values: {
    '7d': 42,
    '30d': 46,
    '90d': 46,
    ytd: 48
  },
  trends: {
    '7d': -2,
    '30d': 3,
    '90d': 1,
    ytd: 5
  },
  previousValues: {
    '7d': 43,
    '30d': 45,
    '90d': 46,
    ytd: 46
  },
  averages: {
    '7d': 43,
    '30d': 44.7,
    '90d': 45,
    ytd: 46
  }
}, {
  label: 'Avg Days to Close',
  values: {
    '7d': 34,
    '30d': 37,
    '90d': 39,
    ytd: 38
  },
  trends: {
    '7d': -5,
    '30d': -2,
    '90d': 3,
    ytd: -1
  },
  previousValues: {
    '7d': 36,
    '30d': 38,
    '90d': 38,
    ytd: 38
  },
  averages: {
    '7d': 36,
    '30d': 38,
    '90d': 38.5,
    ytd: 38.2
  }
}];
const timeframeLabels: Record<Timeframe, string> = {
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
  ytd: 'Year to Date'
};
const comparisonLabels: Record<Timeframe, string> = {
  '7d': 'vs avg week',
  '30d': 'vs avg month',
  '90d': 'vs avg quarter',
  ytd: 'vs prior year'
};
interface MetricCardProps {
  label: string;
  value: number;
  trend: number;
  previousValue: number;
  average: number;
  timeframe: Timeframe;
  isPercentage?: boolean;
  invertTrend?: boolean;
}
const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  trend,
  previousValue,
  average,
  timeframe,
  isPercentage = false,
  invertTrend = false
}) => {
  const effectiveTrend = invertTrend ? -trend : trend;
  const isPositive = effectiveTrend > 0;
  const isNeutral = trend === 0;
  return <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-all h-full flex flex-col" data-id="element-23">
      <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-24">{label}</h3>
      <div className="flex items-end justify-between mb-6" data-id="element-25">
        <div className="text-3xl font-bold text-gray-900 tracking-tight" data-id="element-26">
          {value}
          {isPercentage ? '%' : ''}
        </div>
        <div className="flex flex-col items-end" data-id="element-27">
          <div className={`flex items-center text-sm font-semibold ${isPositive ? 'text-emerald-600' : isNeutral ? 'text-gray-500' : 'text-rose-600'}`} data-id="element-28">
            {isPositive ? <TrendingUp className="w-4 h-4 mr-1" data-id="element-29" /> : isNeutral ? <Minus className="w-4 h-4 mr-1" data-id="element-30" /> : <TrendingDown className="w-4 h-4 mr-1" data-id="element-31" />}
            {trend > 0 ? '+' : ''}
            {trend}%
          </div>
          <span className="text-xs text-gray-400 mt-1" data-id="element-32">
            {comparisonLabels[timeframe]}
          </span>
        </div>
      </div>
      <div className="border-t border-gray-100 pt-4 space-y-2 mt-auto" data-id="element-33">
        <div className="flex justify-between text-sm" data-id="element-34">
          <span className="text-gray-500" data-id="element-35">Previous period</span>
          <span className="text-gray-900 font-medium" data-id="element-36">
            {previousValue}
            {isPercentage ? '%' : ''}
          </span>
        </div>
        <div className="flex justify-between text-sm" data-id="element-37">
          <span className="text-gray-500" data-id="element-38">Average</span>
          <span className="text-gray-900 font-medium" data-id="element-39">
            {average}
            {isPercentage ? '%' : ''}
          </span>
        </div>
        <div className="flex justify-between text-sm pt-1" data-id="element-40">
          <span className="text-gray-500" data-id="element-41">vs Average</span>
          <span className={`font-medium ${value > average ? invertTrend ? 'text-rose-600' : 'text-emerald-600' : value < average ? invertTrend ? 'text-emerald-600' : 'text-rose-600' : 'text-gray-500'}`} data-id="element-42">
            {value > average ? '+' : ''}
            {isPercentage ? (value - average).toFixed(1) : (value - average).toFixed(1)}
          </span>
        </div>
      </div>
    </div>;
};
export const DashboardMetrics: React.FC = () => {
  const [timeframe, setTimeframe] = useState<Timeframe>('30d');
  return <div className="flex flex-col h-full" data-id="element-43">
      {/* Timeframe Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm" data-id="element-44">
        <div className="flex items-center space-x-3" data-id="element-45">
          <div className="p-2 bg-blue-50 rounded-lg" data-id="element-46">
            <CalendarIcon className="w-5 h-5 text-blue-600" data-id="element-47" />
          </div>
          <span className="text-sm font-medium text-gray-700" data-id="element-48">Timeframe:</span>
          <div className="flex bg-gray-100/50 p-1 rounded-lg" data-id="element-49">
            {(Object.keys(timeframeLabels) as Timeframe[]).map(tf => <button key={tf} onClick={() => setTimeframe(tf)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${timeframe === tf ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-50">
                {tf.toUpperCase()}
              </button>)}
          </div>
        </div>
        <div className="flex items-center text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100" data-id="element-51">
          <BarChart3Icon className="w-4 h-4 mr-2 text-gray-400" data-id="element-52" />
          {timeframeLabels[timeframe]}
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-53">
        {metricsData.map(metric => <MetricCard key={metric.label} label={metric.label} value={metric.values[timeframe]} trend={metric.trends[timeframe]} previousValue={metric.previousValues[timeframe]} average={metric.averages[timeframe]} timeframe={timeframe} isPercentage={metric.label === 'Conversion Rate'} invertTrend={metric.label === 'Avg Days to Close'} data-id="element-54" />)}
      </div>
    </div>;
};